import {
  type AccordionValueChangeDetails,
  Accordion as ArkAccordion,
  type AccordionItemContentProps as ArkAccordionItemContentProps,
  type AccordionItemProps as ArkAccordionItemProps,
  type AccordionItemTriggerProps as ArkAccordionItemTriggerProps,
  type AccordionRootProps as ArkAccordionRootProps,
  useAccordionItemContext,
} from "@ark-ui/react"
import cx from "classnames"
import { useId } from "react"
import ToggleIcon from "./ToggleIcon"

export { useAccordionItemContext } from "@ark-ui/react"

type HeadingLevelMapType = {
  h2: 2
  h3: 3
  h4: 4
  h5: 5
  h6: 6
}

type HeadingTag = keyof HeadingLevelMapType

export type AccordionItemHeaderPropsType = {
  /**
   * To improve the semantics of the markup further, the ARIA guidelines dictate
   * that each accordion item's button should be wrapped in an element with
   * role="heading", or more simply, an HTML heading tag.
   */
  headingLevel?: HeadingLevelMapType[HeadingTag]
  className?: string
  leftToggleIcon?: boolean
  useChevron?: boolean
} & ArkAccordionItemTriggerProps

export type AccordionItemPanelPropsType = ArkAccordionItemContentProps

export type AccordionItemPropsType = Omit<ArkAccordionItemProps, "value"> & {
  value?: ArkAccordionItemProps["value"]
}

export type AccordionPropsType = Omit<
  ArkAccordionRootProps,
  "onValueChange"
> & {
  onValueChange?: (value: AccordionValueChangeDetails["value"]) => void
}

const AccordionItemHeader = ({
  children,
  className,
  headingLevel = 4,
  leftToggleIcon,
  useChevron = false,
  ...props
}: AccordionItemHeaderPropsType) => {
  const Heading = `h${headingLevel}` as HeadingTag
  const { expanded } = useAccordionItemContext()

  return (
    <Heading>
      <ArkAccordion.ItemTrigger
        className={cx("cobalt-accordion__header", {
          "cobalt-accordion__header--left-toggle-icon": leftToggleIcon,
        })}
        {...props}
      >
        <span className={cx("cobalt-accordion__header-content", className)}>
          {children}
        </span>
        <ToggleIcon useChevron={useChevron} isOpen={expanded} />
      </ArkAccordion.ItemTrigger>
    </Heading>
  )
}

AccordionItemHeader.displayName = "Accordion.Item.Header"

const AccordionItemPanel = ({
  children,
  className,
  ...props
}: AccordionItemPanelPropsType) => {
  return (
    <ArkAccordion.ItemContent
      className={cx("cobalt-accordion__panel", className)}
      {...props}
    >
      {children}
    </ArkAccordion.ItemContent>
  )
}

AccordionItemPanel.displayName = "Accordion.Item.Panel"

const AccordionItem = ({
  children,
  className,
  value,
  ...props
}: AccordionItemPropsType) => {
  const generatedId = useId()
  const accordionItemId = value || generatedId

  return (
    <ArkAccordion.Item
      className={cx("cobalt-accordions-list__item", className)}
      value={accordionItemId}
      {...props}
    >
      {children}
    </ArkAccordion.Item>
  )
}
AccordionItem.displayName = "Accordion.Item"
AccordionItem.Header = AccordionItemHeader
AccordionItem.Panel = AccordionItemPanel

const Accordion = ({
  className,
  children,
  collapsible = true,
  onValueChange,
  ...props
}: AccordionPropsType) => {
  return (
    <ArkAccordion.Root
      className={cx("cobalt-accordions-list", className)}
      collapsible={collapsible}
      onValueChange={(details) => onValueChange?.(details.value)}
      {...props}
    >
      {children}
    </ArkAccordion.Root>
  )
}

Accordion.displayName = "Accordion"
Accordion.Item = AccordionItem

export default Accordion
